import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ProjectOption } from '@ai-meeting/shared'
import { startBacklogOauth } from '@/services/backlog'
import { fetchProjects } from '@/services/projects'
import { useUiStrings } from '@/hooks/useUiStrings'
import { projectFullLabel, projectOptionLabel } from '@/utils/projectOptionLabel'
import { logActivity } from '@/services/activity'
import {
  BACKLOG_CONNECTED_MESSAGE,
  openBacklogApproval,
  openBacklogApprovalTab,
} from '@/utils/backlogConnectPopup'
import { ExternalLinkIcon, SparklesIcon, XMarkIcon } from './Icons'
import SelectField from './SelectField'

/**
 * `blocked` is not a failure: the approval URL is in hand and the only thing
 * missing is a window to put it in, which the reader can supply themselves.
 *
 * `project` is the second half of the trip. A grant with no project on the
 * meeting answers nothing about tickets, so the two belong in one errand
 * rather than in a dialog and a strip the reader has to notice afterwards.
 */
type Phase = 'idle' | 'opening' | 'waiting' | 'blocked' | 'failed' | 'project' | 'saving'

/** The project list's own outcome, which is not the dialog's phase. */
type ProjectsState = 'loading' | 'ready' | 'empty' | 'error'

interface BacklogConnectDialogProps {
  /** Dismiss, whether or not anything was connected. */
  onClose: () => void
  /**
   * The project already on the meeting.
   *
   * When there is one the dialog closes on the approval instead of asking:
   * there is nothing to choose, and a step whose answer is already filled in
   * is a step that reads as a mistake.
   */
  meetingProjectKey: string | null
  /**
   * Save the chosen project and apply it to the meeting.
   *
   * @returns False when it could not be saved, which keeps the dialog open on
   *   the step that failed rather than closing over the error.
   */
  onChooseProject: (projectKey: string) => Promise<boolean>
  /**
   * Run once the approval has come back, whether or not a project follows.
   *
   * The caller uses it to pick up questions that were refused for want of an
   * account; those become answerable the moment the grant exists.
   */
  onConnected: () => void
}

/**
 * Connect Backlog, and name the project, without leaving the screen that asked
 * (BRAI-689).
 *
 * The screen that asks is usually a meeting that is recording. The approval
 * has to happen on Backlog's own domain, so it gets a window of its own and
 * this dialog waits for it — the meeting keeps running in the tab behind,
 * which is the whole point. Readers used to be sent to Settings in a second
 * tab and come back to a panel that had not noticed.
 *
 * The project is asked for here rather than left to the strip behind: a grant
 * on its own answers nothing about tickets, and a reader who has just finished
 * one errand does not reliably notice a second one appearing behind the dialog
 * they were looking at.
 *
 * Rendered through a portal because the Copilot panel it is opened from is
 * translated on small screens, and a transformed ancestor is what `position:
 * fixed` resolves against — an overlay left inside it would cover the panel
 * rather than the window.
 */
export default function BacklogConnectDialog({
  onClose,
  meetingProjectKey,
  onChooseProject,
  onConnected,
}: BacklogConnectDialogProps) {
  const { t } = useUiStrings()
  const [phase, setPhase] = useState<Phase>('idle')
  const [authorizationUrl, setAuthorizationUrl] = useState<string | null>(null)
  const [projects, setProjects] = useState<ProjectOption[]>([])
  const [projectsState, setProjectsState] = useState<ProjectsState>('loading')
  const [saveFailed, setSaveFailed] = useState(false)

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    function onMessage(event: MessageEvent) {
      // Origin-checked: this listens to every frame on the page, and the only
      // sender it will ever mean is this app's own approval window.
      if (event.origin !== window.location.origin) return
      if (event.data !== BACKLOG_CONNECTED_MESSAGE) return
      onConnected()
      // Nothing left to ask when the meeting already names a project.
      if (meetingProjectKey?.trim()) return onClose()
      setPhase('project')
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('message', onMessage)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('message', onMessage)
    }
  }, [meetingProjectKey, onClose, onConnected])

  // Asked for only once the grant exists: before that the answer is an empty
  // list and a status, which says nothing this dialog could act on.
  useEffect(() => {
    if (phase !== 'project') return undefined
    let cancelled = false
    setProjectsState('loading')
    void fetchProjects()
      .then((response) => {
        if (cancelled) return
        setProjects(response.projects)
        setProjectsState(response.projects.length > 0 ? 'ready' : 'empty')
      })
      .catch(() => {
        if (!cancelled) setProjectsState('error')
      })
    return () => {
      cancelled = true
    }
  }, [phase])

  /**
   * Ask the server where to send the reader, then put it in its own window.
   *
   * The two steps are deliberately not one: a blocked window still leaves a
   * usable URL, and losing it would make the reader start the trip again.
   */
  async function begin() {
    setPhase('opening')
    try {
      const { authorizationUrl: url } = await startBacklogOauth()
      // Counted apart from the Settings button, because which of the two
      // places readers connect from is a thing worth knowing (BRAI-677).
      logActivity('backlog.connect_started_in_copilot')
      setAuthorizationUrl(url)
      setPhase(openBacklogApproval(url) ? 'waiting' : 'blocked')
    } catch {
      logActivity('backlog.connect_started_in_copilot', { status: 'failure' })
      // The message is not shown: `startBacklogOauth` fails when the
      // deployment has no Backlog credentials at all, which is a sentence
      // about the server that means nothing to the person reading it.
      setPhase('failed')
    }
  }

  async function choose(projectKey: string) {
    if (!projectKey) return
    setSaveFailed(false)
    setPhase('saving')
    if (await onChooseProject(projectKey)) return onClose()
    setSaveFailed(true)
    setPhase('project')
  }

  const onProjectStep = phase === 'project' || phase === 'saving'
  const busy = phase === 'opening' || phase === 'waiting' || phase === 'saving'

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/30 px-3 py-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="backlog-connect-dialog-title"
      data-testid="backlog-connect-dialog"
    >
      <button
        type="button"
        aria-label={t.close}
        className="fixed inset-0 cursor-default"
        onClick={onClose}
      />

      <div className="animate-scale-in relative w-full max-w-md rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-5 shadow-2xl sm:p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50 text-[var(--accent)] dark:bg-blue-500/15">
            <SparklesIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2
              id="backlog-connect-dialog-title"
              className="text-lg font-bold text-[var(--text-primary)]"
            >
              {onProjectStep ? t.backlogConnectDialogProjectTitle : t.backlogConnectDialogTitle}
            </h2>
            <p className="mt-2 text-sm leading-5 text-[var(--text-secondary)]">
              {onProjectStep ? t.backlogConnectDialogProjectBody : t.backlogConnectDialogBody}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t.close}
            className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)]"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        {phase === 'waiting' ? (
          <p
            className="mt-4 text-sm text-[var(--text-secondary)]"
            data-testid="backlog-connect-waiting"
            role="status"
          >
            {t.backlogConnectDialogWaiting}
          </p>
        ) : null}

        {phase === 'blocked' ? (
          <p className="mt-4 text-sm text-[var(--text-secondary)]" role="alert">
            {t.backlogConnectDialogBlocked}
          </p>
        ) : null}

        {phase === 'failed' ? (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">
            {t.backlogConnectDialogFailed}
          </p>
        ) : null}

        {onProjectStep ? (
          <div className="mt-4 space-y-2">
            {projectsState === 'error' ? (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                {t.backlogConnectDialogProjectsFailed}
              </p>
            ) : projectsState === 'empty' ? (
              <p className="text-sm text-[var(--text-secondary)]">{t.assistBacklogNoProjects}</p>
            ) : (
              <SelectField
                id="backlog-connect-project"
                label={t.projectLabel}
                labelHidden
                testId="backlog-connect-project-select"
                value=""
                disabled={projectsState === 'loading' || phase === 'saving'}
                onChange={(projectKey) => void choose(projectKey)}
              >
                <option value="">
                  {projectsState === 'loading' ? t.projectLoading : t.projectPlaceholder}
                </option>
                {projects.map((project) => (
                  <option
                    key={project.projectKey}
                    value={project.projectKey}
                    title={projectFullLabel(project.projectKey, project.name)}
                  >
                    {projectOptionLabel(project.projectKey, project.name)}
                  </option>
                ))}
              </SelectField>
            )}
            {saveFailed ? (
              <p
                className="text-sm text-red-600 dark:text-red-400"
                role="alert"
                data-testid="backlog-connect-project-failed"
              >
                {t.copilotSetupApplyFailed}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="backlog-connect-dismiss"
            className="rounded-lg px-4 py-2 text-sm font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)]"
          >
            {onProjectStep ? t.backlogConnectDialogPickLater : t.backlogConnectDialogCancel}
          </button>
          {onProjectStep ? null : phase === 'blocked' && authorizationUrl ? (
            // A press rather than a link: the window still has to be opened
            // with `noopener`, so that this tab's storage does not travel into
            // it and send it back to the meeting afterwards.
            <button
              type="button"
              data-testid="backlog-connect-open-manually"
              onClick={() => openBacklogApprovalTab(authorizationUrl)}
              className="inline-flex items-center gap-1 rounded-lg bg-[var(--accent)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--accent-hover)]"
            >
              {t.backlogConnectDialogOpenManually}
              <ExternalLinkIcon className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button
              type="button"
              data-testid="backlog-connect-start"
              onClick={() => void begin()}
              disabled={busy}
              className="rounded-lg bg-[var(--accent)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--accent-hover)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {t.backlogConnectDialogAction}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
